import { useEffect, useState } from 'react';
import { useSubmit } from 'react-router';
import { Minus, Plus } from 'lucide-react';
import { z } from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UserProfile } from '~/types';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Switch } from '~/components/ui/switch';
import { Label } from '~/components/ui/label';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '~/components/ui/form';
import { userProfilesInsertSchema } from '~/db/schema';

const COUNT_NAMES = [
  'First',
  'Second',
  'Third',
  'Forth',
  'Fifth',
  'Sixth',
  'Sevent',
  'Eleventh',
  'Twelfth',
  'Thirteenth',
  'Fourteenth',
  'Fifteenth',
  'Sixteenth',
  'Seventeenth',
  'Eighteenth',
  'Nineteenth',
  'Twentieth',
];

interface Props {
  profiles: UserProfile[];
  closeSheet: () => void;
}

export function AddNewProfiles({ profiles, closeSheet }: Props) {
  const [isAdding, setIsAdding] = useState(profiles.length === 0);
  const [isSameLastName, setIsSameLastName] = useState(false);

  const formSchema = z.object({
    isSameLastName: z.boolean(),
    profiles: z
      .array(userProfilesInsertSchema)
      .min(1, 'At least one registrant is required'),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isSameLastName: false,
      profiles: [],
    },
    mode: 'onChange',
  });

  const {
    fields: profileFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'profiles',
  });

  const handleDecrease = () => {
    remove(profileFields.length - 1);
  };

  const handleIncrease = () => {
    append({
      firstName: '',
      lastName: isSameLastName ? profileFields[0]?.lastName || '' : '',
    });
  };

  useEffect(
    function allSameChange() {
      if (isSameLastName && profileFields.length > 0) {
        const firstLastName = form.getValues('profiles.0.lastName');
        profileFields.forEach((_, index) => {
          if (index > 0) {
            form.setValue(`profiles.${index}.lastName`, firstLastName);
          }
        });
      }
    },
    [isSameLastName, form, profileFields],
  );

  useEffect(
    function updateLastNames() {
      if (isSameLastName && profileFields.length > 0) {
        const unsubscribe = form.watch((value, { name }) => {
          if (name === 'profiles.0.lastName') {
            const firstLastName = value.profiles?.[0]?.lastName || '';
            profileFields.forEach((_, index) => {
              if (index > 0) {
                form.setValue(`profiles.${index}.lastName`, firstLastName);
              }
            });
          }
        });

        return () => unsubscribe.unsubscribe();
      }
    },
    [isSameLastName, form, profileFields],
  );

  const submit = useSubmit();

  async function onSubmit(data: z.infer<typeof formSchema>) {
    const formData = new FormData();

    formData.append('action', 'addProfiles');
    formData.append('isSameLastName', data.isSameLastName.toString());
    data.profiles.forEach((profile, index) => {
      formData.append(`profiles.${index}.firstName`, profile.firstName);
      formData.append(`profiles.${index}.lastName`, profile.lastName);
    });

    submit(formData, {
      method: 'post',
    });

    closeSheet();
  }

  if (!isAdding) {
    return (
      <div className="flex justify-end">
        <Button onClick={() => setIsAdding(true)}>Add people</Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {profiles.length > 0 && <Label>Select number of people to add:</Label>}
        <section className="flex justify-between pt-2">
          <div className="flex items-center gap-6">
            <Button
              variant="outline"
              size="iconCircular"
              onClick={handleDecrease}
            >
              <Minus />
            </Button>
            <div className="text-4xl">{profileFields.length}</div>
            <Button
              variant="outline"
              size="iconCircular"
              onClick={handleIncrease}
            >
              <Plus />
            </Button>
          </div>
          {profileFields.length > 1 && (
            <FormField
              control={form.control}
              name="isSameLastName"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Switch
                      id="same-last-name"
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value);
                        setIsSameLastName(value);
                      }}
                    />
                  </FormControl>
                  <FormLabel>Same last name</FormLabel>
                </FormItem>
              )}
            />
          )}
        </section>
        <section className="flex flex-col gap-2 pt-4">
          {profileFields.map((field, index) => (
            <div className="flex gap-2" key={field.id}>
              <FormField
                control={form.control}
                name={`profiles.${index}.firstName`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder={`${COUNT_NAMES[index]} persons's first name`}
                        {...field}
                        data-1p-ignore
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`profiles.${index}.lastName`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder={`${COUNT_NAMES[index]} persons's last name`}
                        disabled={index > 0 && isSameLastName}
                        {...field}
                        data-1p-ignore
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ))}
        </section>
        <section className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => {
              setIsAdding(false);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!form.formState.isValid}>
            Save
          </Button>
        </section>
      </form>
    </Form>
  );
}
